import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/daej519tto0ceqqnguc0";

const fieldCls =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [formMsg, setFormMsg] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐：命中则不提交，仅给出与正常一致的反馈
    const trap = (fd.get("phone_alt") as string) || "";
    if (trap.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setErrorMsg("");
    setFormMsg("");

    // 构造干净载荷
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "phone_alt") return;
      payload.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code as string | undefined;
      const msg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormMsg(msg && msg.trim() ? msg : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，暂时无法提交，请稍后再试。");
    }
  }

  return (
    <section id="contact" className="border-t border-background-200 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          {/* 左：说明 */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              Submit a Lead
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-[2rem]">
              提交一条你关注的传闻
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-600">
              看到疑似夸张、断章取义或真假难辨的消息？把它交给我们，我们会按“溯源 → 证据链 → 时间线”三步核验，
              并尽量在结果区给出结论与依据。
            </p>

            <ul className="mt-6 space-y-3">
              {[
                { icon: "ri-shield-keyhole-line", text: "信息只用于求证，不会外泄或恶意扩散。" },
                { icon: "ri-time-line", text: "暂无法证实的线索会如实标注“核实中”，绝不抢发。" },
                { icon: "ri-refresh-line", text: "结论会随证据更新，出现反转会第一时间更正。" },
              ].map((li, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className={`${li.icon} text-sm`} aria-hidden="true"></i>
                  </span>
                  {li.text}
                </li>
              ))}
            </ul>

            <p className="mt-6 text-sm text-foreground-500">
              联系与合作：contact@55heiliaozheng.cn
            </p>
          </div>

          {/* 右：表单 */}
          <form
            data-readdy-form="线索提交与意见反馈"
            onSubmit={onSubmit}
            className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  你的称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={20}
                  placeholder="怎么称呼你"
                  className={fieldCls}
                />
              </div>
              <div>
                <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  线索所属栏目
                </label>
                <select id="category" name="category" className={fieldCls}>
                  <option value="食品安全">食品安全</option>
                  <option value="医疗健康">医疗健康</option>
                  <option value="消费电商">消费电商</option>
                  <option value="政策法规">政策法规</option>
                  <option value="娱乐圈">娱乐圈</option>
                  <option value="社会热点">社会热点</option>
                  <option value="商界争议">商界争议</option>
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                你的邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="用于接收求证结果通知"
                className={fieldCls}
              />
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                事件简述
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="请尽量说明你看到的原话、来源与关键疑点（500 字以内）"
                className={`${fieldCls} resize-none`}
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-400">限 500 字</p>
            </div>

            {/* 蜜罐（对用户不可见，用于拦截机器人） */}
            <input
              type="text"
              name="phone_alt"
              className="opt-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="mt-6">
              <button
                type="submit"
                disabled={status === "loading"}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                    提交线索
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="mt-3 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-line text-base" aria-hidden="true"></i>
                  已收到你的线索，我们会尽快核实并同步结果，感谢参与求证。
                </p>
              )}
              {(status === "error" || formMsg) && (
                <p className="mt-3 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true"></i>
                  {errorMsg || formMsg || "提交失败，请稍后重试。"}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}